import { useQuery, useMutation } from '@tanstack/react-query';
import { makeBackendApi } from 'services/BackendApi';
import { useNotifications } from 'hooks/useNotifications';

/**
 * React Query hook for fetching hotel collection list
 *
 * @param name - Hotel name search (debounced)
 * @param countryCodes - Array of 2-letter country codes (e.g., ["MV", "SC"])
 * @param bestFors - Array of filter enum keys (e.g., ["BEST_FOR_FAMILIES", "BEST_FOR_BEACH"])
 * @returns Query result with hotel list data
 */
export const useExploreHotelsList = (
  name: string,
  countryCodes: string[],
  bestFors: string[]
) => {
  const backendApi = makeBackendApi();

  return useQuery({
    queryKey: ['hotelCollection', 'exploreList', name, countryCodes, bestFors],
    queryFn: async () => {
      const response = await backendApi.exploreHotelsList({
        name: name || undefined,  // Don't send empty string
        countryCodes: countryCodes.length > 0 ? countryCodes : undefined,
        bestFors: bestFors.length > 0 ? bestFors : undefined,
      });
      return response.data;
    },
    staleTime: 0, // Always refetch on mount
  });
};

/**
 * React Query hook for fetching hotel filter options
 * Used to populate the "Best For" filter chips dynamically
 *
 * @returns Query result with filter options (regions, star ratings, filters)
 */
export const useHotelAdminOptions = () => {
  const backendApi = makeBackendApi();

  return useQuery({
    queryKey: ['hotelAdmin', 'options'],
    queryFn: async () => {
      const response = await backendApi.hotelAdminGetOptions();
      return response.data;
    },
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes (options rarely change)
  });
};

/**
 * React Query hook for fetching destination options
 * Uses the same API as the Enquiries/Bookings page to ensure consistent destination list
 * This returns only countries that have bookable hotels
 *
 * @returns Query result with countries array
 */
export const useDestinationOptions = () => {
  const backendApi = makeBackendApi();

  return useQuery({
    queryKey: ['hotelCollection', 'destinationOptions'],
    queryFn: async () => {
      const response = await backendApi.getNamesSearch('');
      return response.data.data.countries;
    },
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes (destinations rarely change)
  });
};

/**
 * React Query hook for fetching hotel collection detail
 *
 * @param hotelUuid - UUID of the hotel to fetch details for
 * @returns Query result with hotel detail data
 */
export const useExploreHotelDetail = (hotelUuid: string) => {
  const backendApi = makeBackendApi();

  return useQuery({
    queryKey: ['hotelCollection', 'detail', hotelUuid],
    queryFn: async () => {
      const response = await backendApi.exploreHotelDetail(hotelUuid);
      return response.data;
    },
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });
};

/**
 * React Query mutation hook for generating explore hotel PDF
 *
 * @param hotelUuid - UUID of the hotel to generate PDF for
 * @returns Mutation result with PDF generation functionality
 */
export const useExploreHotelPdfExport = (hotelUuid: string) => {
  const backendApi = makeBackendApi();
  const notifications = useNotifications();

  return useMutation({
    mutationFn: async () => {
      const response = await backendApi.exploreHotelDetailPdf(hotelUuid);
      return response.data;
    },
    onSuccess: data => {
      if (data.data.url) {
        window.open(data.data.url, '_blank');
        notifications.showSuccessNotification('PDF exported successfully');
      } else {
        notifications.showErrorNotification('Failed to generate PDF. Please try again.');
      }
    },
    onError: error => {
      console.error('Error generating PDF:', error);
      notifications.showErrorNotification('Failed to generate PDF. Please try again.');
    },
  });
};
